import React from "react";
import { Lock, Shield, Sparkles } from "lucide-react";
import { Link, useOutletContext } from "react-router-dom";

export default function PrivacyPolicy() {
  const { settings } = useOutletContext();
  const supportEmail = settings?.general?.supportEmail || "privacy@sugamstore.in";
  const appName = settings?.general?.appName || "Sugam Store";

  return (
    <main className="page-shell content-page policy-page">
      <div className="breadcrumbs">
        <Link to="/">Home</Link>
        <span>/</span>
        <span>Privacy Policy</span>
      </div>

      <div className="policy-header">
        <span className="eyebrow">
          <Sparkles size={14} /> TRUST & DATA SECURITY
        </span>
        <h1>Privacy Policy</h1>
        <p className="policy-meta">Last Updated: October 2026 · Effective Immediately</p>
      </div>

      <div className="policy-container">
        <aside className="policy-nav-card">
          <h3>Table of Contents</h3>
          <nav>
            <a href="#introduction">1. Introduction</a>
            <a href="#information-collection">2. Information We Collect</a>
            <a href="#how-we-use">3. How We Use Your Data</a>
            <a href="#payment-security">4. Payment & Security</a>
            <a href="#cookies">5. Cookies & Analytics</a>
            <a href="#data-sharing">6. Third-Party Sharing</a>
            <a href="#customer-rights">7. Your Privacy Rights</a>
            <a href="#contact-privacy">8. Contact Data Officer</a>
          </nav>
        </aside>

        <article className="policy-body">
          <section id="introduction">
            <h2>1. Introduction</h2>
            <p>
              Welcome to <strong>{appName}</strong>. We value your trust and are committed to safeguarding 
              your personal information. This Privacy Policy details how we collect, store, handle, and 
              protect your personal data when you browse our website, create an account, or make a purchase.
            </p>
            <p>
              By accessing or using our services, you consent to the data practices described in this document. 
              If you do not agree with any part of this policy, please discontinue use of the platform.
            </p>
          </section>

          <section id="information-collection">
            <h2>2. Information We Collect</h2>
            <p>When you interact with {appName}, we may collect the following types of information:</p>
            <ul>
              <li>
                <strong>Contact & Profile Details:</strong> Your name, mobile phone number, email address, 
                and delivery address provided during registration, checkout, or support requests.
              </li>
              <li>
                <strong>Transaction Data:</strong> Details of items purchased, dates, amounts, shipping addresses, 
                and payment confirmation references.
              </li>
              <li>
                <strong>Technical Information:</strong> IP address, browser type, operating system, device details, 
                and approximate geolocation to ensure secure sessions and fraud prevention.
              </li>
              <li>
                <strong>Communications:</strong> Records of your communications with our customer care representatives 
                via email, chat, or phone.
              </li>
            </ul>
          </section>

          <section id="how-we-use">
            <h2>3. How We Use Your Information</h2>
            <p>We use your information strictly for legitimate commercial and fulfillment purposes, including:</p>
            <ul>
              <li>Processing, fulfilling, and dispatching your product orders.</li>
              <li>Sending live order updates, shipping tracking links, and delivery notifications via SMS/Email.</li>
              <li>Providing responsive customer support and processing returns or refunds.</li>
              <li>Preventing fraudulent transactions and ensuring overall account security.</li>
              <li>Improving catalog selections, website speed, and shopping experience.</li>
            </ul>
          </section>

          <section id="payment-security">
            <h2>4. Payment Security & Data Protection</h2>
            <div className="policy-callout">
              <Lock size={20} />
              <div>
                <strong>PCI-DSS Certified Encryption</strong>
                <p>
                  We never store your complete credit/debit card numbers or CVV on our servers. All online payments 
                  are securely processed via leading RBI-authorized payment gateways (e.g. Razorpay) 
                  using 256-bit SSL encryption.
                </p>
              </div>
            </div>
          </section>

          <section id="cookies">
            <h2>5. Cookies & Tracking Technologies</h2>
            <p>
              We use lightweight session cookies and local storage tokens to remember your shopping cart items, 
              keep you authenticated across page refreshes, and remember your site preferences. You may disable 
              cookies in your browser settings, but please note that some interactive features may not function properly.
            </p>
          </section>

          <section id="data-sharing">
            <h2>6. Third-Party Disclosures</h2>
            <p>
              We <strong>do not sell, rent, or trade</strong> your personal information to third-party marketing brokers. 
              We only share relevant information with verified operational partners:
            </p>
            <ul>
              <li><strong>Logistics Partners:</strong> Courier and delivery agents to safely deliver your parcels.</li>
              <li><strong>Payment Processors:</strong> Authorized financial gateways to process transaction settlements.</li>
              <li><strong>Legal Authorities:</strong> Only when strictly mandated by Indian law or judicial court order.</li>
            </ul>
          </section>

          <section id="customer-rights">
            <h2>7. Your Privacy Rights</h2>
            <p>
              You maintain full control over your personal information. You can review and update your name, 
              saved addresses, and contact numbers at any time from your <Link to="/profile">Profile page</Link>. 
              You may also request the deletion of your account and associated records by writing to our team.
            </p>
          </section>

          <section id="contact-privacy">
            <h2>8. Contact Our Privacy Officer</h2>
            <p>
              If you have any questions, clarifications, or concerns regarding this policy or our data practices, 
              please write to our Privacy & Grievance desk:
            </p>
            <p>
              <strong>Email:</strong> <a href={`mailto:${supportEmail}`}>{supportEmail}</a>
              <br />
              <strong>Attention:</strong> Grievance Officer, {appName}
            </p>
          </section>
        </article>
      </div>
    </main>
  );
}
